/**
 * ↑ recalls the last message the rep sent — a team request via Nikhil
 * (2026-09-15): "like in many chat apps". Pure cursor logic behind the
 * composer in CommsThread.jsx, exercised by recall_history.test.js without a
 * DOM. The component owns the textarea; this module only answers "what does
 * this key do to the box?".
 *
 * The rules, as requested:
 *   · with the box empty, ↑ fills it with the rep's most recent own message
 *     in this channel; ↑ again walks further back
 *   · ↓ walks forward and finally back to the empty draft
 *   · Escape or any typing exits recall mode (the text stays as a plain draft;
 *     the component resets the cursor to NOT_RECALLING on both)
 *   · ↑ does nothing while the @mention picker is open (it owns ↑/↓), or when
 *     the caret is not on the first line of a multi-line draft — there ↑ is
 *     caret movement and must reach the textarea
 *
 * "Own" is the thread's is_mine test (a STAFF row whose authorId is me_id),
 * not deleted, with a body. Only rows of the visibility the composer is in
 * are offered: Enter sends client-visible by default, and recalling an
 * internal note into that box would be a leak one keystroke away.
 */

export const NOT_RECALLING = -1;

/** Bodies of the rep's own messages, newest first — the recall list.
 *  `visibility` ('CLIENT_VISIBLE' | 'INTERNAL') narrows to the composer's mode;
 *  omit it for every own row. Identical bodies collapse onto their newest
 *  occurrence: an ↑ that visibly does nothing reads as broken. */
export const own_history = (messages, me_id, visibility) => {
    if (me_id == null || me_id === '') return [];
    const me = String(me_id);
    const rows = Array.isArray(messages) ? messages : [];
    const out = [];
    const seen = new Set();
    for (let i = rows.length - 1; i >= 0; i -= 1) {
        const m = rows[i];
        if (!m || m.authorType !== 'STAFF' || m.deletedAt || m.authorId == null || String(m.authorId) !== me) continue;
        if (visibility && m.visibility !== visibility) continue;
        const body = typeof m.body === 'string' ? m.body : '';
        if (!body.trim() || seen.has(body)) continue;
        seen.add(body);
        out.push(body);
    }
    return out;
};

/** True when no newline precedes the caret — it sits on the first line. */
export const caret_on_first_line = (value, caret) => {
    const at = Number(caret) || 0;
    if (at <= 0) return true;
    return String(value == null ? '' : value).lastIndexOf('\n', at - 1) === -1;
};

/** True when no newline follows the caret — it sits on the last line. */
export const caret_on_last_line = (value, caret) => String(value == null ? '' : value).indexOf('\n', Number(caret) || 0) === -1;

/**
 * Where ↑ / ↓ takes the recall cursor. Returns {idx, draft} for the component
 * to apply (and preventDefault), or null when the key is not ours and must
 * reach the textarea as ordinary caret movement.
 *   key           e.key
 *   idx           the cursor: NOT_RECALLING, or the history index in the box
 *   history       own_history(...) — newest first
 *   draft         the box's current text
 *   caret         {first_line, last_line} — where the caret is in the box
 *   mention_open  the @mention picker is up
 */
export const recall_move = (key, { idx, history, draft, caret, mention_open }) => {
    if (mention_open || !Array.isArray(history) || !history.length) return null;
    const text = typeof draft === 'string' ? draft : '';
    // The box holds exactly the recalled entry → we are recalling. Anything
    // typed (or a list that changed under us) makes the cursor stale, and ↑/↓
    // go back to being caret keys.
    const recalling = idx >= 0 && idx < history.length && text === history[idx];
    const where = caret || {};
    if (key === 'ArrowUp') {
        if (!text.trim()) return { idx: 0, draft: history[0] };
        if (!recalling || !where.first_line) return null;
        // At the oldest entry ↑ stays put but is still ours — the caret must not jump.
        const next = Math.min(idx + 1, history.length - 1);
        return { idx: next, draft: history[next] };
    }
    if (key === 'ArrowDown') {
        if (!recalling || !where.last_line) return null;
        return idx === 0 ? { idx: NOT_RECALLING, draft: '' } : { idx: idx - 1, draft: history[idx - 1] };
    }
    return null;
};
